import {Injectable} from '@angular/core';
import {BehaviorSubject, mergeMap, noop, Observable, tap} from "rxjs";
import {EventDetail, EventInfo} from "../models/event";
import {BaseServiceContextService} from "./base-service-context.service";
import {Params} from "../models/params";
import {Pageable} from "../models/pageable";
import {HttpParams} from "@angular/common/http";
import {Store} from "@ngrx/store";
import {AppState} from "../../store/app.state";
import {OrganizationModel} from "../../main/organization/organization.model";
import {Passage} from "../models/passage";
import {VirtualDevicesService} from "./virtual-devices.service";
@Injectable({
  providedIn: 'root'
})
export class EventService {
  eventList: EventInfo[] = [];
  eventListSubject: BehaviorSubject<EventInfo[]> = new BehaviorSubject<EventInfo[]>([]);
  listOfIds: number[] = [];
  public selectedEventSubject = new BehaviorSubject<EventDetail>({});

  constructor(private baseServiceContext: BaseServiceContextService,
              private vdService: VirtualDevicesService,
              private store: Store<AppState>) {
  }

  getById(id: number): Observable<EventInfo> {
    return this.getEventById(id)
      .pipe(tap(event => {
        if (event) {
          const index = this.eventList?.findIndex(e => e.id === id);
          if (index == -1) {
            this.eventList.push(event);
          }
        }
        this.eventListSubject.next(this.eventList);
      }));
  }

  getEventById(id: number): Observable<EventInfo> {
    return this.baseServiceContext.http
      .get<EventInfo>(`${this.baseServiceContext.baseUrl}/events/${id}/`)
  }


  getEventDetail(id: number): Observable<EventDetail> {
    return this.getEventById(id).pipe(
      mergeMap(async (event) => {
        if (!event) return {};
        const currentEventDetail: EventDetail = {
          event,
          id: event.id,
        };

        // Fetch all properties in parallel
        const [areas, device_manager_settings, my_permissions, passages, devices] = await Promise.all([
              this.getAPI('areas',id),
          this.getAPI('device_manager_settings',id),
          this.getAPI('my_permissions',id),
          this.getAPI('passages',id),
          this.getDevicesList(id)
        ]);

        // Update currentEventDetail
        currentEventDetail.areas = areas;
        currentEventDetail.device_manager_settings = device_manager_settings;
        currentEventDetail.my_permissions = my_permissions;

        if (passages) {
          const fullPassages = await Promise.all(
            passages.map(async (passage: Passage) => {
              const fullVirtualDevices = await Promise.all(
                passage.virtual_devices.map(vdId => this.vdService.getById(vdId))
              );
              passage.virtual_devices = fullVirtualDevices;
              return passage;
            })
          );
          currentEventDetail.passages = fullPassages;
        }

        currentEventDetail.devices = devices;

        // Now emit ONCE after all data is fully ready
        this.selectedEventSubject.next(currentEventDetail);

        return currentEventDetail;
      })
    );
  }


  // should be deleted
  getEventsDetailWithOutStore(): Observable<any> {
    return this.selectedEventSubject;
  }

  updateStore(){
    // this.store.dispatch(selectedEvent({ event: this.selectedEventSubject.value }));
  }



  getEventsList(): Observable<EventInfo[]> {
    return this.eventListSubject;
  }
  removeEvent(){
    this.selectedEventSubject.next({});
  }


  updateEventDetailProperty(propertyName: keyof EventDetail, apiUrl: string) {
    this.baseServiceContext.http.get<any>(apiUrl).pipe(
      mergeMap(async result => {  // << use mergeMap to handle Promise
        if (result) {
          const currentEventDetail: EventDetail = { ...this.selectedEventSubject.value };
          currentEventDetail[propertyName] = result;

          if (propertyName === 'passages') {
            const passages: Passage[] = result;

            const passagePromises = passages.map(async (passage) => {
              const fullVirtualDevices = await Promise.all(
                passage.virtual_devices.map(vdId => this.vdService.getById(vdId))
              );
              passage.virtual_devices = fullVirtualDevices;
              return passage;
            });

            await Promise.all(passagePromises);
          }

          this.selectedEventSubject.next(currentEventDetail);
        }
      })
    ).subscribe();
  }



  createEvent(form: EventInfo) : Promise<any>{
    return this.baseServiceContext.http.post<any>(`${this.baseServiceContext.baseUrl}/events/`, form).toPromise();
  }

    getAPI(name: string, eventId: number) : Promise<any>{
    return this.baseServiceContext.http.get<any>(`${this.baseServiceContext.baseUrl}/events/${eventId}/${name}/`).toPromise();
  }


  getDevicesList(eventId: number) : Promise<any>{
    return this.baseServiceContext.http.get<any>(`${this.baseServiceContext.baseUrl}/events/${eventId}/devices/`).toPromise();
  }


  fetchAllData(form: Params): Promise<Pageable | undefined> {
    let queryParams = new HttpParams();

    if (form) {
      Object.entries(form).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams = queryParams.append(key, String(value));
        }
      });
    }

    return this.baseServiceContext.http
      .get<Pageable | undefined>(`${this.baseServiceContext.baseUrl}/events/`, { params: queryParams })
      .toPromise();
  }

  edit(form: any, eventId: number) : Observable<EventInfo |undefined>{
    return this.baseServiceContext.http
      .put<EventInfo | undefined>(`${this.baseServiceContext.baseUrl}/events/${eventId}/`, form)

  }

  delete( eventId: number) : Promise<EventInfo |undefined>{
    return this.baseServiceContext.http
      .delete<EventInfo | undefined>(`${this.baseServiceContext.baseUrl}/events/${eventId}/`).toPromise()

  }

}
